import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata: { role: string; full_name: string, requested_airline_id: string }) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track whether user has actively signed in during this page session.
    // This prevents a stale-session 403 from firing SIGNED_OUT and
    // nuking a freshly-created session in another tab/context.
    let hasActiveSession = false;

    // Get initial session and validate it server-side.
    // autoRefreshToken is OFF to prevent the Supabase client from racing
    // with a stale token before we can validate and clear it.
    console.log('[AuthContext] Initializing - fetching session...');
    supabase.auth.getSession().then(async ({ data: { session: cachedSession } }) => {
      if (cachedSession) {
        // Validate the cached session against the server
        const { data: { user: validUser }, error } = await supabase.auth.getUser();
        if (error || !validUser) {
          // Cached session is stale (e.g. session deleted server-side).
          // Clear it locally so the Supabase client stops retrying.
          console.warn('[AuthContext] Cached session invalid, clearing:', error?.message);
          await supabase.auth.signOut({ scope: 'local' });
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        console.log('[AuthContext] Session validated:', cachedSession.user.email);
        hasActiveSession = true;
        setSession(cachedSession);
        setUser(cachedSession.user);
        // Session is valid — now safe to enable auto-refresh
        supabase.auth.startAutoRefresh();
      } else {
        console.log('[AuthContext] No cached session');
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only log significant auth events, not every token refresh
      if (event !== 'TOKEN_REFRESHED') {
        console.log('[AuthContext] Auth state changed:', event, session ? `User: ${session.user.email}` : 'No session');
      }

      if (event === 'SIGNED_IN') {
        hasActiveSession = true;
        // Enable auto-refresh for the new session
        supabase.auth.startAutoRefresh();
      }

      // Ignore SIGNED_OUT events that are triggered by stale-session 403s
      // when we already have an active session (prevents cross-tab session nuking)
      if (event === 'SIGNED_OUT' && !session && hasActiveSession) {
        console.warn('[AuthContext] Ignoring SIGNED_OUT from stale session — active session exists');
        // Re-validate: check if we still have a valid session
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
          if (currentSession) {
            // We still have a valid session — the SIGNED_OUT was from a stale token
            console.log('[AuthContext] Confirmed active session still valid, ignoring stale logout');
            return;
          }
          // No valid session left — this is a real sign-out
          console.log('[AuthContext] No valid session found, processing sign-out');
          hasActiveSession = false;
          setSession(null);
          setUser(null);
          setLoading(false);
        });
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_OUT') {
        hasActiveSession = false;
      }

      // Only set loading to false on initial session or sign in/out events
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: { role: string; full_name: string, requested_airline_id: string }) => {
    try {
      // Sign up user - database trigger will automatically create profile
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata, // This metadata is stored in auth.users.raw_user_meta_data
        },
      });

      if (error) return { error };

      // Profile is automatically created by database trigger 'handle_new_user()'
      // No need to manually create organization or user profile

      return { error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('[AuthContext] signIn called for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('[AuthContext] signIn error:', error);
    } else {
      console.log('[AuthContext] signIn successful:', data.user?.email);
      console.log('[AuthContext] Session:', data.session ? 'Present' : 'Missing');
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

